import { RedemptionBy } from "../constants";
import { FolioWiseHolding } from "../types";
import { formatAsCrore } from "./currency";

/**
 * Calculate the future value of an amount considering inflation
 *
 * Formula: FV = PV × (1 + r)^n
 * Where:
 * - FV = Future Value
 * - PV = Present Value (target amount in today's terms)
 * - r = inflation rate (as decimal)
 * - n = number of years (duration)
 *
 * @param targetAmount - The target amount in today's value
 * @param expectedInflation - Expected inflation rate (e.g., 6 for 6%)
 * @param durationMonths - Duration in months
 * @returns The future value adjusted for inflation
 *
 * @example
 * // Calculate future value of ₹10,00,000 with 6% inflation over 10 years
 * const futureAmount = calculateFutureValue(1000000, 6, 10);
 * // Returns: ₹17,90,847.70
 */
export function calculateFutureValue(
  targetAmount: number,
  expectedInflation: number,
  durationMonths: number
): number {
  if (targetAmount <= 0 || durationMonths < 0) {
    return 0;
  }

  if (!expectedInflation || expectedInflation === 0) {
    return targetAmount;
  }

  // Convert percentage to decimal (e.g., 6% -> 0.06)
  const inflationRate = expectedInflation / 100;

  // Apply compound interest formula: FV = PV × (1 + r)^n
  const futureValue =
    targetAmount * (1 + inflationRate) ** (durationMonths / 12);

  return Math.round(futureValue * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate the present value of a future amount (reverse of calculateFutureValue)
 *
 * Formula: PV = FV / (1 + r)^n
 *
 * @param futureAmount - The future amount
 * @param expectedInflation - Expected inflation rate (e.g., 6 for 6%)
 * @param durationYears - Duration in years
 * @returns The present value adjusted for inflation
 *
 * @example
 * // Calculate present value of ₹17,90,847.70 with 6% inflation over 10 years
 * const presentAmount = calculatePresentValue(1790847.70, 6, 10);
 * // Returns: ₹10,00,000
 */
export function calculatePresentValue(
  futureAmount: number,
  expectedInflation: number,
  durationYears: number
): number {
  if (futureAmount <= 0 || durationYears < 0) {
    return 0;
  }

  if (expectedInflation === 0) {
    return futureAmount;
  }

  // Convert percentage to decimal
  const inflationRate = expectedInflation / 100;

  // Apply discounting formula: PV = FV / (1 + r)^n
  const presentValue = futureAmount / (1 + inflationRate) ** durationYears;

  return Math.round(presentValue * 100) / 100; // Round to 2 decimal places
}

/**
 * SIP step-up config. Every `frequencyMonths` the installment grows by
 * `percentage` % (compounding) or by a flat `amount`.
 */
export type SipStepUp = {
  amount?: number | null;
  percentage?: number | null;
  frequencyMonths?: number | null;
};

export type ProposalStepUpFields = {
  step_up_amount?: string | number | null;
  step_up_percentage?: string | number | null;
  step_up_frequency?: number | null;
};

// A proposal's saved step-up as understood by the SIP math helpers. The ₹
// amount is per fund, so it's scaled by the number of SIP funds.
export function getProposalStepUp(
  values: ProposalStepUpFields,
  sipFundCount: number = 1
): SipStepUp | null {
  if (!values.step_up_frequency) return null;
  const amount = Number(values.step_up_amount) || 0;
  const percentage = Number(values.step_up_percentage) || 0;
  if (!amount && !percentage) return null;
  return {
    amount: amount ? amount * Math.max(sipFundCount, 1) : null,
    percentage: amount ? null : percentage,
    frequencyMonths: values.step_up_frequency,
  };
}

// e.g. "₹5,000 / 6 months" or "10% / 12 months"; null when step-up is off
export function formatProposalStepUp(values: ProposalStepUpFields) {
  if (!values.step_up_frequency) return null;
  const amount = Number(values.step_up_amount) || 0;
  const percentage = Number(values.step_up_percentage) || 0;
  if (!amount && !percentage) return null;
  const increase = amount
    ? formatAsCrore(amount, 0)
    : `${parseFloat(percentage.toFixed(2))}%`;
  return `${increase} / ${values.step_up_frequency} months`;
}

const isStepUpActive = (stepUp?: SipStepUp | null): stepUp is SipStepUp =>
  !!stepUp?.frequencyMonths &&
  (Number(stepUp.amount) > 0 || Number(stepUp.percentage) > 0);

/**
 * Splits a stepped-up SIP's value into `base * PMT + increment * stepUpAmount`,
 * so it can be solved for the starting installment. Installments are
 * invested at the start of each month (annuity due), matching
 * `calculateExpectedCorpus`. Pass `monthlyRate = 0` to get invested totals.
 */
function stepUpSipFactors(
  monthlyRate: number,
  durationMonths: number,
  stepUp: SipStepUp
) {
  const frequency = Number(stepUp.frequencyMonths);
  const percentage = Number(stepUp.percentage) || 0;
  const usePercentage = !(Number(stepUp.amount) > 0);
  let base = 0;
  let increment = 0;
  for (let m = 0; m < durationMonths; m += 1) {
    const steps = Math.floor(m / frequency);
    const growth = (1 + monthlyRate) ** (durationMonths - m);
    if (usePercentage) {
      base += (1 + percentage / 100) ** steps * growth;
    } else {
      base += growth;
      increment += steps * growth;
    }
  }
  return {
    base,
    increment,
    stepUpAmount: usePercentage ? 0 : Number(stepUp.amount),
  };
}

/**
 * Total amount invested through a SIP over the duration, including step-ups.
 */
export function calculateTotalSipInvested(
  monthlySIP: number,
  durationMonths: number,
  stepUp?: SipStepUp | null
): number {
  if (monthlySIP <= 0 || durationMonths <= 0) return 0;
  if (!isStepUpActive(stepUp)) return monthlySIP * durationMonths;
  const { base, increment, stepUpAmount } = stepUpSipFactors(
    0,
    durationMonths,
    stepUp
  );
  return Math.round((monthlySIP * base + stepUpAmount * increment) * 100) / 100;
}

export type SipJourneyPeriod = {
  /** 0-based month offsets from the first installment, inclusive */
  startMonth: number;
  endMonth: number;
  /** Installment for this period; changes only on step-up boundaries */
  monthlySIP: number;
  /** Total invested from the start through the end of this period */
  cumulativeInvested: number;
  /** Expected SIP value at the end of this period */
  expectedValue: number;
};

/**
 * Month-by-month SIP growth, shared by the SIP calculators and proposals.
 * Breaks the SIP into consecutive periods of `periodMonths` (the step-up
 * frequency when step-up is active). Values are unrounded; round for display.
 */
export function calculateSipJourney(
  monthlySIP: number,
  expectedReturn: number,
  durationMonths: number,
  stepUp?: SipStepUp | null,
  periodMonths: number = 12
): SipJourneyPeriod[] {
  if (monthlySIP <= 0 || durationMonths <= 0) return [];
  const active = isStepUpActive(stepUp);
  const period = active ? Number(stepUp.frequencyMonths) : periodMonths;
  const monthlyRate = (1 + expectedReturn / 100) ** (1 / 12) - 1;

  const periods: SipJourneyPeriod[] = [];
  let invested = 0;
  let value = 0;
  // Stepped up after each period, same as the SIP calculator
  let installment = monthlySIP;
  for (let start = 0; start < durationMonths; start += period) {
    if (start > 0 && active) {
      if (Number(stepUp.amount) > 0) installment += Number(stepUp.amount);
      else installment *= 1 + Number(stepUp.percentage) / 100;
    }
    const end = Math.min(start + period, durationMonths) - 1;
    for (let m = start; m <= end; m += 1) {
      invested += installment;
      value = (value + installment) * (1 + monthlyRate);
    }
    periods.push({
      startMonth: start,
      endMonth: end,
      monthlySIP: installment,
      cumulativeInvested: invested,
      expectedValue: value,
    });
  }
  return periods;
}

/**
 * Calculate the required monthly SIP amount to reach a goal
 *
 * Formula: PMT = FV × (r / ((1 + r)^n - 1))
 * Where r is monthly rate and n is number of months
 *
 * @param targetAmount - Target corpus amount
 * @param expectedReturn - Expected annual return rate (e.g., 12 for 12%)
 * @param durationMonths - Duration in months
 * @param upfrontAmount - Optional upfront lump sum investment
 * @param stepUp - Optional step-up; the result is the starting installment
 * @returns Required monthly SIP amount
 *
 * @example
 * // Calculate monthly SIP for ₹50,00,000 with 12% returns over 10 years
 * const monthlySIP = calculateMonthlySIP(5000000, 12, 10);
 */
export function calculateMonthlySIP(
  targetAmount: number,
  expectedReturn: number,
  durationMonths: number,
  upfrontAmount: number = 0,
  stepUp?: SipStepUp | null
): number {
  if (targetAmount <= 0 || durationMonths <= 0) {
    return 0;
  }

  // Convert annual rate to monthly rate: (1 + annual)^(1/12) - 1
  const annualRate = expectedReturn / 100;
  const monthlyRate = (1 + annualRate) ** (1 / 12) - 1;
  const months = durationMonths;

  // Calculate future value of upfront amount
  let adjustedTarget = targetAmount;
  if (upfrontAmount > 0 && expectedReturn > 0) {
    const upfrontFV = upfrontAmount * (1 + monthlyRate) ** months;
    adjustedTarget = targetAmount - upfrontFV;
  } else if (upfrontAmount > 0) {
    adjustedTarget = targetAmount - upfrontAmount;
  }

  if (adjustedTarget <= 0) {
    return 0; // Upfront amount already covers the target
  }

  if (isStepUpActive(stepUp)) {
    const { base, increment, stepUpAmount } = stepUpSipFactors(
      expectedReturn > 0 ? monthlyRate : 0,
      months,
      stepUp
    );
    const monthlySIP = (adjustedTarget - stepUpAmount * increment) / base;
    return Math.max(Math.round(monthlySIP * 100) / 100, 0);
  }

  if (expectedReturn === 0) {
    // Simple division if no returns expected
    return Math.round((adjustedTarget / months) * 100) / 100;
  }

  // FV = PMT × [((1 + r)^n - 1) / r] × (1 + r)
  // Therefore: PMT = FV × r / [((1 + r)^n - 1) × (1 + r)]
  const denominator = ((1 + monthlyRate) ** months - 1) * (1 + monthlyRate);
  const monthlySIP = adjustedTarget * (monthlyRate / denominator);

  return Math.round(monthlySIP * 100) / 100;
}

/**
 * Calculate the corpus amount achievable with given SIP and lump sum
 *
 * @param monthlySIP - Monthly SIP amount
 * @param upfrontAmount - Upfront lump sum investment
 * @param expectedReturn - Expected annual return rate (e.g., 12 for 12%)
 * @param durationMonths - Duration in months
 * @param stepUp - Optional step-up applied to the SIP
 * @returns Expected corpus amount
 * FV = PMT × [((1 + r)^n - 1) / r] × (1 + r)
 */
export function calculateExpectedCorpus(
  monthlySIP: number,
  upfrontAmount: number,
  expectedReturn: number,
  durationMonths: number,
  stepUp?: SipStepUp | null
): number {
  if (durationMonths <= 0) {
    return 0;
  }

  // Convert annual rate to monthly rate: (1 + annual)^(1/12) - 1
  const annualRate = expectedReturn / 100;
  const monthlyRate = (1 + annualRate) ** (1 / 12) - 1;
  const months = durationMonths;

  let corpus = 0;

  // Future value of upfront amount (annual compounding)
  if (upfrontAmount > 0) {
    if (expectedReturn > 0) {
      corpus += upfrontAmount * (1 + monthlyRate) ** months;
    } else {
      corpus += upfrontAmount;
    }
  }

  // Future value of SIP
  if (monthlySIP > 0 && isStepUpActive(stepUp)) {
    const { base, increment, stepUpAmount } = stepUpSipFactors(
      expectedReturn > 0 ? monthlyRate : 0,
      months,
      stepUp
    );
    corpus += monthlySIP * base + stepUpAmount * increment;
  } else if (monthlySIP > 0) {
    if (expectedReturn > 0) {
      // FV = PMT × [((1 + r)^n - 1) / r] × (1 + r)
      const sipFV =
        monthlySIP *
        (((1 + monthlyRate) ** months - 1) / monthlyRate) *
        (1 + monthlyRate);
      corpus += sipFV;
    } else {
      corpus += monthlySIP * months;
    }
  }

  return Math.round(corpus * 100) / 100;
}

export function calculateCapitalGains(
  folio: FolioWiseHolding,
  redemptionBy: RedemptionBy,
  value: string
) {
  if (!folio) return null;

  if (!value) return null;

  const {
    ltcg_units: ltcgUnits,
    stcg_units: stcgUnits,
    ltcg,
    stcg,
    nav,
  } = folio;

  const ltcgUnitsAvailable = Number(ltcgUnits) || 0;
  const stcgUnitsAvailable = Number(stcgUnits) || 0;
  const ltcgAmount = Number(ltcg) || 0;
  const stcgAmount = Number(stcg) || 0;

  // Calculate gain per unit for LTCG and STCG
  const ltcgRatePerUnit =
    ltcgUnitsAvailable > 0 ? ltcgAmount / ltcgUnitsAvailable : 0;
  const stcgRatePerUnit =
    stcgUnitsAvailable > 0 ? stcgAmount / stcgUnitsAvailable : 0;

  // Determine units to redeem based on redemption type
  let unitsToRedeem: number;
  if (redemptionBy === RedemptionBy.AMOUNT) {
    const amountNum = Number(value);
    if (!value || !amountNum || Number.isNaN(amountNum)) return null;
    // Convert amount to units using NAV
    unitsToRedeem = amountNum / Number(nav);
  } else {
    const unitsNum = Number(value);
    if (!value || !unitsNum || Number.isNaN(unitsNum)) return null;
    unitsToRedeem = unitsNum;
  }

  // Guard against invalid calculations
  if (!unitsToRedeem || Number.isNaN(unitsToRedeem) || unitsToRedeem <= 0) {
    return null;
  }

  // Calculate applicable LTCG and STCG (FIFO: LTCG units redeemed first)
  const ltcgUnitsRedeemed = Math.min(unitsToRedeem, ltcgUnitsAvailable);
  const remainingUnits = Math.max(0, unitsToRedeem - ltcgUnitsRedeemed);
  const stcgUnitsRedeemed = Math.min(remainingUnits, stcgUnitsAvailable);

  const applicableLtcg = ltcgRatePerUnit * ltcgUnitsRedeemed;
  const applicableStcg = stcgRatePerUnit * stcgUnitsRedeemed;

  return {
    ltcg: applicableLtcg,
    stcg: applicableStcg,
  };
}

const RESTRICTION_MESSAGE_PREFIX = "Scheme is currently not accepting ";
const LUMP_SUM_TEXT = "One time ";
const AND_TEXT = "and ";
const SIP_TEXT = "SIP ";
const RESTRICTION_MESSAGE_SUFFIX = "investments.";

export const buildRestrictionMessage = (
  isOneTimeDisabled: boolean,
  isSipDisabled: boolean
): string => {
  const parts: string[] = [RESTRICTION_MESSAGE_PREFIX];

  if (isOneTimeDisabled) {
    parts.push(LUMP_SUM_TEXT);
  }

  if (isOneTimeDisabled && isSipDisabled) {
    parts.push(AND_TEXT);
  }

  if (isSipDisabled) {
    parts.push(SIP_TEXT);
  }

  parts.push(RESTRICTION_MESSAGE_SUFFIX);

  return parts.join("");
};
